import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Lock } from 'lucide-react'
import { useState } from 'react'
import { generatePath, Link, Navigate, useOutletContext, useSearchParams } from 'react-router-dom'

import type { RegionId } from '@snootsvet/shared'
import type { IncompletePayment, SubscriptionPlan } from '@snootsvet/shared/portal'
import {
  HEALTHY_SUBSCRIPTION_STATUSES,
  REGIONS_BY_ID,
  subscriptionPlanSchema,
} from '@snootsvet/shared/portal'

import { PaymentMethods } from '@/customer/components/billing/payment-methods'
import { PaymentReconciliationFailed } from '@/customer/components/billing/payment-reconciliation-failed'
import { SelectedSubscriptionPlanCard } from '@/customer/components/billing/selected-subscription-plan-card'
import { StripePaymentWidget } from '@/customer/components/billing/stripe-payment-widget'
import { findPlanDiscount } from '@/customer/components/billing/subscription-plans'
import { ContactSupportAction } from '@/customer/components/contact-support-action'
import { LoadingSpinner } from '@/customer/components/loading-spinner'
import { PageContainer } from '@/customer/components/page-container'
import { PetPreviewCard } from '@/customer/components/pet/pet-preview-card'
import type { PetScopeContext } from '@/customer/components/pet-scope'
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/customer/components/ui/alert'
import { Button } from '@/customer/components/ui/button'
import { useCreateSubscription } from '@/customer/hooks/use-create-subscription'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { petDiscountsQueryKey, useGetPetDiscounts } from '@/customer/hooks/use-get-pet-discounts'
import { petSubscriptionQueryKey } from '@/customer/hooks/use-get-pet-subscription'
import { invoicesQueryKey } from '@/customer/hooks/use-list-invoices'
import { useListPlans } from '@/customer/hooks/use-list-plans'
import { useStripe } from '@/customer/hooks/use-stripe'
import { useTimeout } from '@/customer/hooks/use-timeout'
import { captureEvent } from '@/customer/lib/posthog'
import { routes } from '@/customer/routes'

const SETTLEMENT_TIMEOUT_MS = 10_000

/**
 * Encapsulates the checkout flow: creating a subscription, attempting
 * immediate payment confirmation, and transitioning to recovery when
 * the initial charge fails.
 *
 * After a successful card confirmation the guard's polling detects
 * the status change and redirects to the welcome page automatically.
 */
function useCheckout(opts: {
  petId: string
  plan: SubscriptionPlan | null
  memberRegion: RegionId
  couponId: string | undefined
}) {
  const { petId, plan, memberRegion, couponId } = opts
  const queryClient = useQueryClient()
  const stripe = useStripe(memberRegion)
  const subscribe = useCreateSubscription(petId)
  /**
   * After Stripe confirms the card charge, the guard polls our API's
   * subscription endpoint waiting for it to flip to `active`.
   * If it hasn't reconciled within the timeout window, reports
   * to Sentry and surfaces a terminal error to the member.
   */
  const reconciliation = useTimeout(SETTLEMENT_TIMEOUT_MS, () => {
    Sentry.captureException(
      new Error('Subscription not healthy after successful card confirmation'),
      { extra: { petId } }
    )
  })
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null)
  const [attemptToken, setAttemptToken] = useState(() => crypto.randomUUID())
  const [incompletePayment, setIncompletePayment] = useState<IncompletePayment | null>(null)
  const [initialCardError, setInitialCardError] = useState<string | null>(null)
  const [confirmingCard, setConfirmingCard] = useState(false)

  /** Subscription data is awaited because the next render routes
   *  based on subscription status; invoices are not routing-critical here.
   */
  async function invalidateAfterSubscribing() {
    await queryClient.invalidateQueries({ queryKey: petSubscriptionQueryKey(petId) })
    queryClient.invalidateQueries({ queryKey: petDiscountsQueryKey() })
    queryClient.invalidateQueries({ queryKey: invoicesQueryKey() })
  }

  async function confirm() {
    if (!paymentMethodId || !plan) return

    try {
      const response = await subscribe.mutateAsync({
        couponId,
        plan,
        paymentMethodId,
        attemptToken,
      })

      if (HEALTHY_SUBSCRIPTION_STATUSES.has(response.subscription.status)) {
        captureEvent('subscription_started', { patient_id: petId, plan })
        await invalidateAfterSubscribing()
        return
      }

      if (response.incompletePayment?.clientSecret && stripe) {
        setConfirmingCard(true)
        const result = await stripe.confirmCardPayment(response.incompletePayment.clientSecret, {
          payment_method: paymentMethodId,
        })

        if (result.error) {
          setInitialCardError(result.error.message ?? 'Payment failed')
          setIncompletePayment(response.incompletePayment)
          return
        }

        await handlePaymentConfirmed()
        return
      }

      if (response.incompletePayment) {
        setIncompletePayment(response.incompletePayment)
        return
      }

      await invalidateAfterSubscribing()
    } catch {
      // mutateAsync re-throws; catch prevents unhandled rejection.
      // Error state is available via subscribe.error.
    } finally {
      setConfirmingCard(false)
      setAttemptToken(crypto.randomUUID())
    }
  }

  // Mutation in flight, Stripe card confirmation in flight, or card
  // confirmed and waiting for the guard's polling to pick up the
  // healthy subscription status.
  const isProcessing = subscribe.isPending || confirmingCard || reconciliation.isPending
  const checkoutStarted = subscribe.isSuccess

  async function handlePaymentConfirmed() {
    captureEvent('subscription_started', { patient_id: petId, plan })
    reconciliation.start()
    await invalidateAfterSubscribing()
  }

  return {
    paymentMethodId,
    setPaymentMethodId,
    incompletePayment,
    initialCardError,
    isProcessing,
    checkoutStarted,
    reconciliationFailed: reconciliation.hasElapsed,
    subscribeError: subscribe.error,
    confirm,
    handlePaymentConfirmed,
  }
}

/**
 * Portal-native checkout page.
 *
 * Reached from `PetPlanPage` once the customer picks a plan (passed
 * as a `?plan=` query param). Creates the subscription and, when
 * the initial charge requires 3DS or is declined, handles payment
 * recovery inline via `StripePaymentWidget` so the customer can
 * complete SCA or try a different card without leaving the page.
 *
 * Redirects away if the pet already has a healthy subscription.
 */
export function PetCheckoutPage() {
  useDocumentTitle('Confirm subscription')
  const { pet, member } = useOutletContext<PetScopeContext>()
  const [searchParams] = useSearchParams()
  const planParam = searchParams.get('plan')

  const parsedPlan = planParam ? subscriptionPlanSchema.safeParse(planParam) : null
  const plan = parsedPlan?.success ? parsedPlan.data : null
  const plans = useListPlans()
  const discounts = useGetPetDiscounts(pet.id)
  const discount = plan ? findPlanDiscount(discounts.data?.discounts ?? [], plan) : undefined
  const checkout = useCheckout({
    petId: pet.id,
    plan,
    memberRegion: member.region,
    couponId: discount?.couponId,
  })

  if (!plan) {
    return <Navigate replace to={generatePath(routes.account.pets.plan, { patientId: pet.id })} />
  }

  // Awaited so the selected plan's price renders once, already discounted where it applies.
  if (plans.isPending || discounts.isPending) {
    return (
      <PageContainer className="items-center justify-center">
        <LoadingSpinner />
      </PageContainer>
    )
  }

  // Only a failed first load blocks the page: confirming without prices would charge an amount
  // the member hasn't seen, and a failed refetch keeps its data so it can't hide the payment step.
  if (!plans.data || !discounts.data) {
    return (
      <PageContainer>
        <Alert variant="warning">
          <AlertTitle>We couldn&apos;t load your plan&apos;s price</AlertTitle>
          <AlertDescription>Please try again shortly.</AlertDescription>
          <AlertAction>
            <ContactSupportAction />
          </AlertAction>
        </Alert>
      </PageContainer>
    )
  }

  if (!plans.data.plans.some((p) => p.plan === plan)) {
    return <Navigate replace to={generatePath(routes.account.pets.plan, { patientId: pet.id })} />
  }

  return (
    <PageContainer
      backLink={
        checkout.checkoutStarted
          ? undefined
          : {
              to: generatePath(routes.account.pets.plan, { patientId: pet.id }),
              label: 'Back to plan',
            }
      }
      headline={[
        { text: 'Ready when', color: 'green' },
        { text: 'you are', color: 'orange' },
      ]}
    >
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PetPreviewCard
            pet={pet}
            region={REGIONS_BY_ID[member.region]}
            className="bg-pink"
            editHref={
              checkout.checkoutStarted ? undefined : `${routes.account.pets.index}?pet=${pet.id}`
            }
          />

          <SelectedSubscriptionPlanCard
            plan={plan}
            region={member.region}
            plans={plans.data.plans}
            discount={discount}
            editHref={
              checkout.checkoutStarted
                ? undefined
                : generatePath(routes.account.pets.plan, { patientId: pet.id })
            }
          />
        </div>

        <div className="mt-2">
          {checkout.reconciliationFailed ? (
            <PaymentReconciliationFailed
              message={`We had some trouble setting up ${pet.name}'s subscription`}
            />
          ) : checkout.isProcessing ? (
            <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-6">
              <LoadingSpinner />
              <p className="text-sm text-muted-foreground">Setting up subscription…</p>
            </div>
          ) : checkout.incompletePayment ? (
            <StripePaymentWidget
              clientSecret={checkout.incompletePayment.clientSecret}
              hostedInvoiceUrl={null}
              label="Complete payment"
              region={member.region}
              initialError={checkout.initialCardError ?? undefined}
              onSuccess={checkout.handlePaymentConfirmed}
            />
          ) : (
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <h2 className="text-sm font-medium text-muted-foreground">Payment method</h2>
                <PaymentMethods
                  value={checkout.paymentMethodId}
                  onChange={checkout.setPaymentMethodId}
                />
              </div>

              <Button
                type="button"
                onClick={checkout.confirm}
                disabled={!checkout.paymentMethodId || checkout.isProcessing}
                className="h-14 w-full"
              >
                <Lock className="size-5" />
                {checkout.isProcessing ? 'Confirming…' : 'Confirm subscription'}
              </Button>

              {checkout.subscribeError ? (
                checkout.subscribeError.name === 'ConflictError' ? (
                  <Alert variant="info">
                    <AlertCircle />
                    <AlertDescription>
                      Looks like {pet.name} already has a membership!{' '}
                      <Link
                        to={`${routes.account.pets.index}?pet=${pet.id}`}
                        className="underline underline-offset-2"
                      >
                        View it here
                      </Link>
                      .
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert variant="destructive">
                    <AlertCircle />
                    <AlertTitle>
                      {checkout.subscribeError.name === 'InternalError' ||
                      !checkout.subscribeError.message
                        ? "Something's gone wrong on our end"
                        : 'Something went wrong'}
                    </AlertTitle>
                    <AlertDescription>
                      {checkout.subscribeError.name === 'InternalError' ||
                      !checkout.subscribeError.message
                        ? 'The team has been notified. Please try again in a few moments.'
                        : checkout.subscribeError.message}
                    </AlertDescription>
                  </Alert>
                )
              ) : null}
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  )
}
